import { useQuery } from '@tanstack/react-query';
import React, { Suspense, lazy, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { partnerApi, type Partner } from '../../lib/api';
import { fetchAllTenants } from '../tenant-selector/useTenants';
import { Select, type SelectOption } from '../ui/select';
import { ApiReferenceLoadingState } from './ApiReferenceLoadingState';
import styles from './ApiReferencePanel.module.css';

const ApiReferenceViewer = lazy(() => import('./ApiReferenceViewer'));

/** Test tokens live for an hour; refresh ahead of expiry so an open tab keeps working. */
const TEST_TOKEN_REFRESH_MS = 50 * 60 * 1000;

const TENANT_ROSTER_STALE_MS = 5 * 60 * 1000;

const DESCRIPTION =
  'Every Reach API endpoint, with the resource endpoints typed by your own schema definitions.';

interface TenantChoice {
  organizationId: string | undefined;
  tenantId: string;
}

/** Another tab can switch the session's organization, so every query here is keyed by it. */
export const ApiReferencePanel: React.FC = () => {
  const organizationId = useAuth().organization?.id;
  const { data, isLoading, error } = useQuery({
    queryKey: ['partnerApiReferenceDocument', organizationId],
    queryFn: () => partnerApi.getPartnerApiReferenceDocument(),
    staleTime: 0,
  });
  const { data: partner } = useQuery<Partner>({
    queryKey: ['partner', organizationId],
    queryFn: () => partnerApi.getPartner(),
  });
  const isSandbox = partner?.accountType === 'sandbox';
  const { data: testToken } = useQuery({
    queryKey: ['partnerApiTestToken', organizationId],
    queryFn: () => partnerApi.createPartnerApiTestToken(),
    enabled: isSandbox,
    staleTime: TEST_TOKEN_REFRESH_MS,
    refetchInterval: TEST_TOKEN_REFRESH_MS,
    retry: false,
  });
  const { data: tenants = [] } = useQuery({
    queryKey: ['tenants', organizationId],
    queryFn: fetchAllTenants,
    staleTime: TENANT_ROSTER_STALE_MS,
  });
  const [tenantChoice, setTenantChoice] = useState<TenantChoice>();
  const isChoiceForThisOrganization = tenantChoice?.organizationId === organizationId;
  const chosenTenantId = isChoiceForThisOrganization ? tenantChoice?.tenantId : undefined;

  const tenantOptions: SelectOption[] = tenants.map(tenant => ({
    value: tenant.externalId ?? tenant.id,
    label: tenant.externalId ? `${tenant.name} (${tenant.externalId})` : tenant.name,
  }));
  const testTenantId = chosenTenantId ?? tenantOptions[0]?.value ?? '';

  if (isLoading) return <ApiReferenceLoadingState />;

  if (error || !data) {
    return (
      <p role="alert" className={styles.error}>
        Your API reference could not be loaded. Try again in a moment.
      </p>
    );
  }

  return (
    <section>
      <header className={styles.header}>
        {isSandbox ? (
          <p className={styles.intro}>
            {DESCRIPTION} Test requests carry a one-hour partner token and act for the tenant you pick
            here.
          </p>
        ) : (
          <p className={styles.intro}>
            {DESCRIPTION} Sending test requests from here is available on sandbox partner accounts.
          </p>
        )}
        {isSandbox && (
          <label className={styles.tenantPicker}>
            Test as tenant
            <Select
              options={tenantOptions}
              value={testTenantId}
              onChange={value =>
                setTenantChoice(value ? { organizationId, tenantId: value } : undefined)
              }
              placeholder="Choose a tenant"
              searchable
              className="min-w-[250px]"
            />
          </label>
        )}
      </header>
      <div className={styles.viewer}>
        <Suspense fallback={<ApiReferenceLoadingState />}>
          <ApiReferenceViewer
            openApiDocument={data}
            partnerToken={testToken?.token}
            tenantId={isSandbox ? testTenantId : undefined}
            allowTestRequests={isSandbox}
          />
        </Suspense>
      </div>
    </section>
  );
};
